<script lang="ts">
  import { onMount } from 'svelte';
  import Login from './components/Login.svelte';
  import Registro from './components/Registro.svelte';
  import DemoClinica from './components/DemoClinica.svelte';
  import AppShell from './layouts/AppShell.svelte';
  import AppViews from './AppViews.svelte';
  import AgendarCitaPublico from './components/AgendarCitaPublico.svelte';
  import BillingTrialBanner from './components/BillingTrialBanner.svelte';
  import BillingTrialExpired from './components/BillingTrialExpired.svelte';
  import BillingSuccess from './components/BillingSuccess.svelte';
  import BillingCancel from './components/BillingCancel.svelte';
  import PlatformConsole from './components/PlatformConsole.svelte';
  import ClinicOnboardingTour from './components/ClinicOnboardingTour.svelte';
  import { session, logout, bootstrapAuth } from './lib/auth.svelte';
  import { isKeycloakMode } from './lib/keycloak';
  import { isPilotMode } from './lib/pilot';
  import { tenantCtx, loadTenantContext, clearTenantContext } from './lib/tenant.svelte';
  import { fetchBillingStatus, trialUrgency, type BillingStatus } from './lib/billing';
  import { resolveDisplayName, resolveBrandMark, applyTenantBranding, loadLocalBranding } from './lib/branding.svelte';
  import {
    buildTourSteps,
    consumeOnboardingPending,
    isOnboardingDone,
  } from './lib/onboarding';
  import {
    clinicPreview,
    effectiveOrgTipo,
    previewModulesFor,
    isDemoTenant,
  } from './lib/clinic-preview.svelte';
  import {
    type Tab,
    pickNavCatalog,
    filterNavSections,
    clinicTabsForOrg,
    isClinicOrgTipo,
    type NavSection,
  } from './lib/navigation';
  import { FISIO_DEMO_EVAL_TAB_KEY } from './lib/clinic-demo-seed';

  const _path = typeof location !== 'undefined' ? location.pathname : '/';
  const _rutaPublica = _path.startsWith('/registro') || _path.startsWith('/login') || _path.startsWith('/demo') || _path.startsWith('/agendar')
    || _path.startsWith('/billing/success') || _path.startsWith('/billing/cancel')
    || _path.startsWith('/admin/plataforma');

  onMount(() => {
    if ((isKeycloakMode() || isPilotMode()) && !_rutaPublica) {
      void bootstrapAuth().catch((err) => console.error('[auth] bootstrap falló', err));
    }
  });

  let tab = $state<Tab>('dashboard');
  let tourOpen = $state(false);
  let tourCheckedFor = $state<string | null>(null);

  const ADMIN_ROLES = ['admin-tenant', 'admin-platform'];
  const KC_SYSTEM_ROLES = new Set(['offline_access', 'uma_authorization', 'user']);

  const rolTabs: Record<string, Tab[]> = {
    recepcion: ['dashboard', 'agenda', 'pacientes', 'episodios', 'expediente', 'ingresos', 'cuentas', 'semaforo'],
    medico: ['dashboard', 'agenda', 'pacientes', 'episodios', 'expediente', 'signos', 'laboratorio', 'imagenologia', 'quirofano', 'ingresos', 'cuentas', 'compartido'],
    enfermeria: ['dashboard', 'agenda', 'expediente', 'signos', 'kardex', 'camas', 'ingresos', 'semaforo', 'altas'],
    farmacia: ['dashboard', 'farmacia', 'farmaciaPos'],
    laboratorio: ['dashboard', 'laboratorio', 'compartido'],
    imagenologia: ['dashboard', 'imagenologia', 'compartido'],
    quirofano: ['dashboard', 'quirofano'],
    caja: ['dashboard', 'agenda', 'cuentas', 'farmaciaPos', 'aseguradoras'],
    bodega: ['bodega'],
    tesoreria: ['tesoreria'],
    contabilidad: ['contabilidad'],
    nomina: ['nomina'],
    mspas: ['reportes'],
    auditoria: ['pacientes', 'episodios', 'expediente', 'signos', 'laboratorio', 'imagenologia', 'cuentas', 'compartido', 'reportes', 'bitacora'],
    'consultorio-externo': ['agenda', 'pacientes', 'episodios', 'expediente', 'signos', 'laboratorio', 'imagenologia', 'cuentas', 'compartido', 'consultorios'],
    'vet-medico': ['dashboard', 'agenda', 'expedienteVet', 'veterinaria', 'vetInternamiento', 'vetMostrador', 'vetFarmacia', 'farmaciaPos', 'cuentas'],
    'vet-recepcion': ['dashboard', 'agenda', 'expedienteVet', 'veterinaria', 'vetInternamiento', 'vetMostrador', 'vetFarmacia', 'farmaciaPos', 'cuentas'],
    'vet-farmacia': ['dashboard', 'vetMostrador', 'vetFarmacia', 'farmaciaPos', 'cuentas'],
    'optica-optometrista': ['dashboard', 'agenda', 'pacientes', 'expediente', 'optica', 'opticaOrdenes', 'opticaInventario', 'farmaciaPos', 'cuentas'],
    'optica-recepcion': ['dashboard', 'agenda', 'pacientes', 'expediente', 'optica', 'opticaOrdenes', 'opticaInventario', 'farmaciaPos', 'cuentas'],
    'optica-ventas': ['dashboard', 'opticaOrdenes', 'opticaInventario', 'farmaciaPos', 'cuentas'],
  };

  const especialidadTabs: Partial<Record<string, Tab[]>> = {
    'clinica-veterinaria': [
      'dashboard', 'agenda', 'expedienteVet', 'veterinaria', 'vetInternamiento',
      'vetMostrador', 'vetFarmacia', 'farmaciaPos', 'cuentas', 'aseguradoras',
      'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
    ],
    'clinica-optica': [
      'dashboard', 'agenda', 'pacientes', 'expediente', 'optica', 'opticaOrdenes',
      'opticaInventario', 'farmaciaPos', 'cuentas', 'aseguradoras',
      'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
    ],
  };

  /** Clínicas privadas: menú por rubro vía navigation.ts (sin hospital ni red B2B). */

  const ROL_LABEL: Record<string, string> = {
    'admin-tenant': 'Administrador', 'admin-platform': 'Plataforma',
    recepcion: 'Recepción', medico: 'Médico', enfermeria: 'Enfermería',
    farmacia: 'Farmacia', laboratorio: 'Laboratorio', imagenologia: 'Imagenología',
    quirofano: 'Quirófano', caja: 'Caja', bodega: 'Bodega', tesoreria: 'Tesorería',
    contabilidad: 'Contabilidad', nomina: 'Nómina', mspas: 'MSPAS',
    auditoria: 'Auditoría', 'consultorio-externo': 'Consultorio externo',
    'vet-medico': 'Vet · Médico', 'vet-recepcion': 'Vet · Recepción', 'vet-farmacia': 'Vet · Farmacia',
    'optica-optometrista': 'Óptica · Optometrista', 'optica-recepcion': 'Óptica · Recepción', 'optica-ventas': 'Óptica · Ventas',
  };

  const ROL_PRIORIDAD = ['admin-platform', 'admin-tenant', 'medico', 'enfermeria', 'recepcion',
    'farmacia', 'laboratorio', 'imagenologia', 'quirofano', 'caja', 'contabilidad',
    'tesoreria', 'bodega', 'nomina', 'mspas', 'auditoria', 'consultorio-externo',
    'vet-medico', 'vet-recepcion', 'vet-farmacia',
    'optica-optometrista', 'optica-recepcion', 'optica-ventas'];

  function rolesAplicacion(roles: string[]): string[] {
    return roles.filter(r => !KC_SYSTEM_ROLES.has(r) && !r.startsWith('default-roles-'));
  }

  function tabsPermitidas(): Set<Tab> | null {
    if (isPilotMode()) return null;
    const tenantId = session.value?.tenantId ?? '';
    if (isDemoTenant(tenantId)) return null;

    const roles = rolesAplicacion(session.value?.roles ?? []);
    if (roles.length === 0) return null;
    if (roles.some(r => ADMIN_ROLES.includes(r))) return null;

    const permitidas = new Set<Tab>();
    let reconocido = false;
    for (const r of roles) {
      const ts = rolTabs[r];
      if (ts) { reconocido = true; ts.forEach(t => permitidas.add(t)); }
    }
    if (!reconocido) return null;
    return permitidas;
  }

  function tabsPermitidasPorEspecialidad(org: string | null): Set<Tab> | null {
    if (!org) return null;
    const vetOptica = especialidadTabs[org];
    if (vetOptica) return new Set<Tab>(vetOptica);
    if (isClinicOrgTipo(org) || clinicPreview.org) {
      return clinicTabsForOrg(org, tenantCtx.edicion);
    }
    if (tenantCtx.isHospitalEdition) return null;
    return clinicTabsForOrg(org, tenantCtx.edicion);
  }

  const effectiveOrg = $derived(
    effectiveOrgTipo(
      tenantCtx.orgTipo as import('./lib/navigation').OrgTipo | null,
      tenantCtx.isHospitalEdition,
      session.value?.tenantId,
    ),
  );

  const modulosEfectivos = $derived.by(() => {
    // Preview rubro clínico: solo módulos ambulatorios (no mezclar con hospital demo).
    if (clinicPreview.org) {
      return previewModulesFor(clinicPreview.org);
    }
    const base = tenantCtx.modulosActivos ?? [];
    return base.length ? base : null;
  });

  const navSections = $derived.by((): NavSection[] => {
    const catalog = pickNavCatalog(tenantCtx.edicion, effectiveOrg);
    return filterNavSections(catalog, {
      modulosActivos: modulosEfectivos,
      permittedTabs: tabsPermitidas(),
      specialtyTabs: tabsPermitidasPorEspecialidad(effectiveOrg),
    });
  });

  const allVisibleTabs = $derived(navSections.flatMap(s => s.items.map(i => i.id)));

  function rolPrincipal(): string | null {
    const roles = session.value?.roles ?? [];
    for (const r of ROL_PRIORIDAD) if (roles.includes(r)) return ROL_LABEL[r] ?? r;
    return null;
  }

  function irModulo(mod: string) {
    const t = mod as Tab;
    if (allVisibleTabs.includes(t)) tab = t;
    else if (allVisibleTabs.includes('dashboard')) tab = 'dashboard';
  }

  function onNavigate(t: Tab) {
    tab = t;
  }

  function salir() {
    logout();
    clinicPreview.org = null;
    try {
      sessionStorage.removeItem('toscana.clinicPreview');
    } catch { /* ignore */ }
  }

  $effect(() => {
    if (!session.isAuth) return;
    try {
      if (sessionStorage.getItem(FISIO_DEMO_EVAL_TAB_KEY)) {
        tab = 'signos';
        sessionStorage.removeItem(FISIO_DEMO_EVAL_TAB_KEY);
      }
    } catch { /* ignore */ }
  });

  $effect(() => {
    const s = session.value;
    if (!s) {
      clearTenantContext();
      return;
    }
    void loadTenantContext(s.tenantId);
    // Restaurar identidad visual guardada localmente
    try {
      const local = loadLocalBranding();
      if (local.primaryColor) {
        applyTenantBranding(local as any, tenantCtx.orgTipo);
      }
    } catch { /* branding no critico */ }
  });

  $effect(() => {
    if (!allVisibleTabs.length) return;
    if (!allVisibleTabs.includes(tab)) {
      tab = allVisibleTabs.includes('dashboard') ? 'dashboard' : allVisibleTabs[0];
    }
  });

  const esLogin = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/login'));
  const esRegistro = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/registro'));
  const esAdminPlataforma = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/admin/plataforma'));
  const esDemoClinica = $derived(typeof location !== 'undefined' && /^\/demo\/clinica\//.test(location.pathname));
  const esDemoLegacy = $derived(typeof location !== 'undefined' && location.pathname === '/demo');

  $effect(() => {
    if (esDemoLegacy && typeof location !== 'undefined') location.replace('/');
  });
  const esAgendar = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/agendar'));
  const esBillingTrialExpired = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/billing/trial-expired'));
  const esBillingSuccess = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/billing/success'));
  const esBillingCancel = $derived(typeof location !== 'undefined' && location.pathname.startsWith('/billing/cancel'));

  let billingStatus = $state<BillingStatus | null>(null);
  $effect(() => {
    if (session.isAuth) {
      fetchBillingStatus().then(s => billingStatus = s).catch(() => billingStatus = null);
    }
  });
  const trialDays = $derived(billingStatus?.diasRestantesTrial ?? null);
  const trialStatus = $derived(billingStatus?.estado ?? null);

  const displayName = $derived(
    resolveDisplayName(tenantCtx.branding, tenantCtx.nombre, session.value?.tenantId ?? 'Toscana'),
  );
  const brandMark = $derived(
    resolveBrandMark(
      tenantCtx.branding,
      session.value?.tenantId ?? '',
      displayName,
      effectiveOrg,
    ),
  );
  const tourSteps = $derived(
    buildTourSteps(displayName, effectiveOrg, new Set(allVisibleTabs)),
  );

  function startTour() {
    tab = 'dashboard';
    tourOpen = true;
  }

  $effect(() => {
    const tid = session.value?.tenantId;
    if (!session.isAuth || !tid || isDemoTenant(tid)) return;
    if (tourCheckedFor === tid) return;
    if (!tenantCtx.snapshot) return;
    tourCheckedFor = tid;
    const pending = consumeOnboardingPending(tid);
    if (pending || !isOnboardingDone(tid)) {
      setTimeout(() => { tourOpen = true; }, 700);
    }
  });
</script>

{#if esAdminPlataforma}
  <PlatformConsole />
{:else if esLogin}
  <Login />
{:else if esRegistro}
  <Registro />
{:else if esBillingTrialExpired}
  {#if session.isAuth}
    <BillingTrialExpired />
  {:else}
    <Login />
  {/if}
{:else if esBillingSuccess}
  <BillingSuccess />
{:else if esBillingCancel}
  <BillingCancel />
{:else if esAgendar}
  <AgendarCitaPublico />
{:else if esDemoClinica}
  <DemoClinica />
{:else if esDemoLegacy}
  <p class="redirect-hint">Redirigiendo…</p>
{:else if !session.isAuth}
  <Login />
{:else}
  <AppShell
    sections={navSections}
    activeTab={tab}
    tenantName={displayName}
    tenantId={session.value!.tenantId}
    brandMark={brandMark}
    orgTipo={effectiveOrg}
    planSlug={tenantCtx.plan}
    roleLabel={rolPrincipal()}
    usuario={session.value!.usuario}
    onNavigate={onNavigate}
    onLogout={salir}
    onStartTour={isDemoTenant(session.value!.tenantId) ? undefined : startTour}
    trialDays={trialDays}
    trialStatus={trialStatus}
  >
    <BillingTrialBanner />
    <AppViews {tab} onNavigate={irModulo} />
  </AppShell>
  <ClinicOnboardingTour
    bind:open={tourOpen}
    steps={tourSteps}
    tenantId={session.value!.tenantId}
    onNavigate={(t) => { irModulo(t); }}
  />
{/if}

<style>
  :global(:root) {
    --aqua: var(--brand);
    --aqua-dark: var(--brand-strong);
    --aqua-deep: var(--brand-deep);
    --aqua-soft: var(--brand-soft);
    --ink: var(--text-primary);
    --muted: var(--text-secondary);
    --line: var(--border);
    --bg: var(--bg-base);
    --card: var(--bg-surface);
  }
  :global(body) {
    margin: 0;
    font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    background: var(--bg-base);
    color: var(--text-primary);
  }
  :global(a) { color: var(--brand-deep); }
</style>
